import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {Object} AluxCustomerData
 * @property {boolean} isLoggedIn
 * @property {number} [id]
 * @property {string} [email]
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string} [phone]
 * @property {string} [fullName]
 * @property {{address1?: string, address2?: string, city?: string, province?: string, phone?: string}} [defaultAddress]
 * @property {string} loginUrl
 */

/** @type {AluxCustomerData} */
// @ts-ignore - AluxCustomer is defined in Liquid template
const AluxCustomer = window.AluxCustomer;

/**
 * Address Drawer Component - extends DialogComponent like CartDrawer
 * Uses a slide system for login check, address form, and wilaya selection
 * Requires customer login before accessing the address form
 * @extends {DialogComponent}
 */
class AddressDrawerComponent extends DialogComponent {
  /** @type {'login' | 'form' | 'wilaya' | 'city'} */
  #currentView = 'form';
  
  /** @type {boolean} */
  #requiresLogin = false;
  
  /** @type {string | null} */
  #customerId = null;

  /** @type {string | null} - Currently selected wilaya code */
  #selectedWilayaCode = null;

  /**
   * Format Algerian phone number to local format (0XXXXXXXXX)
   * Handles +213 international prefix and removes spaces/dashes
   * @param {string} phone - The phone number to format
   * @returns {string} - Formatted phone number
   */
  #formatAlgerianPhone(phone) {
    if (!phone) return '';
    
    if (phone.startsWith('+213')) {
      // Remove +213 prefix and any immediate spaces/dashes, then strip all spaces
      const withoutPrefix = phone.replace(/^\+213[\s-]*/, '').replace(/\s+/g, '');
      return '0' + withoutPrefix;
    }
    
    // For non-international format, just remove spaces
    return phone.replace(/\s+/g, '');
  }

  connectedCallback() {
    super.connectedCallback();
    
    // DEBUG: Check if wilayas data is available at component load
    console.log('[AddressDrawer] connectedCallback - ALUX_WILAYAS:', !!window.ALUX_WILAYAS);
    console.log('[AddressDrawer] connectedCallback - getCommunesByWilaya:', typeof window.getCommunesByWilaya);
    if (window.ALUX_WILAYAS) {
      console.log('[AddressDrawer] ALUX_WILAYAS length:', window.ALUX_WILAYAS.length);
    }
    
    // Check if login is required (attribute only present if NOT logged in)
    this.#requiresLogin = this.hasAttribute('data-requires-login');
    this.#customerId = this.getAttribute('data-customer-id');
    
    // Pre-fill form with customer data if logged in
    if (!this.#requiresLogin) {
      this.#prefillFromCustomer();
    }
    
    this.#loadSavedAddress();
    
    // Attach wilaya selector button click handler
    const wilayaButton = /** @type {HTMLElement} */ (this.refs.wilayaButton);
    if (wilayaButton) {
      wilayaButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openWilayaSheet();
      });
    }
    
    // Attach wilaya list click handler using event delegation
    const wilayaList = /** @type {HTMLElement} */ (this.refs.wilayaList);
    if (wilayaList) {
      wilayaList.addEventListener('click', (/** @type {Event} */ e) => this.selectWilaya(e));
    }
    
    // Attach city selector button click handler
    const cityButton = /** @type {HTMLElement} */ (this.refs.cityButton);
    if (cityButton) {
      cityButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!cityButton.hasAttribute('disabled')) {
          this.openCitySheet();
        }
      });
    }
    
    // Attach city list click handler using event delegation
    const cityList = /** @type {HTMLElement} */ (this.refs.cityList);
    if (cityList) {
      cityList.addEventListener('click', (/** @type {Event} */ e) => this.selectCity(e));
    }
    
    // Setup shop-login-button event listener
    this.#setupLoginButton();
    
    // FORCE set the correct slide position on load
    this.#forceSlidePosition();
    
    // Check if we should auto-open the drawer (returning from login)
    this.#checkAutoOpen();
  }
  
  /**
   * Force the correct slide position using inline styles
   */
  #forceSlidePosition() {
    const slidesContainer = /** @type {HTMLElement} */ (this.refs.slidesContainer);
    if (!slidesContainer) return;
    
    // Remove all classes first and clear any inline transform
    slidesContainer.style.transform = '';
    slidesContainer.classList.remove('show-login', 'show-wilaya', 'show-city');
    
    if (this.#requiresLogin) {
      // Show login slide
      slidesContainer.classList.add('show-login');
    }
    // Default (no class) = form view at -25%
  }
  
  /**
   * Setup the shop-login-button to redirect after login
   */
  #setupLoginButton() {
    const loginSlide = /** @type {HTMLElement} */ (this.refs.loginSlide);
    if (!loginSlide) return;
    
    // Build return URL with auto-open parameter
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set('open_drawer', 'address');
    
    // Setup shop-login-button
    const shopLoginButton = loginSlide.querySelector('shop-login-button');
    if (shopLoginButton) {
      shopLoginButton.setAttribute('return-uri', returnUrl.toString());
      shopLoginButton.addEventListener('completed', () => {
        // Also save to localStorage as backup
        localStorage.setItem('alux_open_drawer_after_login', 'true');
        window.location.href = returnUrl.toString();
      });
    }
    
    // Also update the fallback login link
    const fallbackButton = /** @type {HTMLAnchorElement | null} */ (this.refs.loginFallbackButton);
    if (fallbackButton) {
      const loginUrl = new URL(fallbackButton.href);
      loginUrl.searchParams.set('return_url', returnUrl.toString());
      fallbackButton.href = loginUrl.toString();
      
      // Add click handler to save localStorage backup
      fallbackButton.addEventListener('click', () => {
        localStorage.setItem('alux_open_drawer_after_login', 'true');
      });
    }
  }
  
  /**
   * Check if drawer should auto-open (returning from login)
   */
  #checkAutoOpen() {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldOpenFromUrl = urlParams.get('open_drawer') === 'address';
    const shouldOpenFromStorage = localStorage.getItem('alux_open_drawer_after_login') === 'true';
    
    if (shouldOpenFromUrl || shouldOpenFromStorage) {
      // Clear the markers
      if (shouldOpenFromUrl) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('open_drawer');
        window.history.replaceState({}, '', cleanUrl.toString());
      }
      localStorage.removeItem('alux_open_drawer_after_login');
      
      // Open drawer after page is fully loaded
      if (document.readyState === 'complete') {
        setTimeout(() => this.showDialog(), 100);
      } else {
        window.addEventListener('load', () => {
          setTimeout(() => this.showDialog(), 100);
        });
      }
    }
  }
  
  /**
   * Pre-fill form fields with customer data from Shopify account
   */
  #prefillFromCustomer() {
    if (!AluxCustomer?.isLoggedIn) return;
    
    const customer = AluxCustomer;
    const defaultAddr = customer.defaultAddress;
    const nameInput = /** @type {HTMLInputElement | null} */ (this.refs.nameInput);
    const emailInput = /** @type {HTMLInputElement | null} */ (this.refs.emailInput);
    const phoneInput = /** @type {HTMLInputElement | null} */ (this.refs.phoneInput);
    const addressInput = /** @type {HTMLInputElement | null} */ (this.refs.addressInput);
    const wilayaInput = /** @type {HTMLInputElement | null} */ (this.refs.wilayaInput);
    const wilayaText = /** @type {HTMLElement | null} */ (this.refs.wilayaText);
    
    // Pre-fill email if empty
    if (emailInput && !emailInput.value && customer.email) {
      emailInput.value = customer.email;
    }
    
    // Pre-fill name if empty
    if (nameInput && !nameInput.value) {
      const fullName = customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
      if (fullName) nameInput.value = fullName;
    }
    
    // Pre-fill phone: prefer defaultAddress phone, fallback to customer phone
    if (phoneInput && !phoneInput.value) {
      const phone = defaultAddr?.phone || customer.phone || '';
      if (phone) {
        phoneInput.value = this.#formatAlgerianPhone(phone);
      }
    }
    
    // Pre-fill address from defaultAddress
    if (addressInput && !addressInput.value && defaultAddr?.address1) {
      let address = defaultAddr.address1;
      if (defaultAddr.address2) {
        address += ', ' + defaultAddr.address2;
      }
      addressInput.value = address;
    }
    
    // Pre-fill wilaya from defaultAddress (city or province)
    if (wilayaInput && !wilayaInput.value && defaultAddr) {
      const wilaya = defaultAddr.province || defaultAddr.city || '';
      if (wilaya) {
        wilayaInput.value = wilaya;
        if (wilayaText) {
          wilayaText.textContent = wilaya;
        }
      }
    }
  }

  /**
   * Check if customer has a complete default address
   * @returns {boolean}
   */
  #hasCompleteDefaultAddress() {
    const customer = AluxCustomer;
    if (!customer?.isLoggedIn || !customer.defaultAddress) return false;
    
    const addr = customer.defaultAddress;
    const phone = addr.phone || customer.phone;
    const wilaya = addr.province || addr.city;
    
    // Need at least: name, phone, address, wilaya
    return !!(customer.fullName && phone && addr.address1 && wilaya);
  }

  /**
   * Directly submit order using customer's default address
   */
  #submitWithDefaultAddress() {
    const customer = AluxCustomer;
    if (!customer?.defaultAddress) return;
    
    const addr = customer.defaultAddress;
    
    const phone = this.#formatAlgerianPhone(addr.phone || customer.phone || '');
    
    let address = addr.address1 || '';
    if (addr.address2) {
      address += ', ' + addr.address2;
    }
    
    const addressData = {
      name: customer.fullName,
      phone: phone,
      address: address,
      wilaya: addr.province || addr.city || '',
      timestamp: Date.now(),
      ...(customer.id && { customerId: customer.id }),
      customerEmail: customer.email
    };

    localStorage.setItem('cod_address', JSON.stringify(addressData));
    window.location.href = '/pages/order-summary';
  }

  /**
   * Opens the address drawer - skips if customer has complete default address
   */
  showDialog() {
    // If logged in with complete address, skip drawer and go directly to order
    if (this.#hasCompleteDefaultAddress()) {
      this.#submitWithDefaultAddress();
      return;
    }
    
    this.#openDrawer();
  }

  /**
   * Force open the drawer for editing (used on order-summary page)
   * Bypasses the auto-submit for users with complete addresses
   */
  showDialogForEdit() {
    this.#openDrawer();
  }

  /**
   * Internal method to actually open the drawer
   */
  #openDrawer() {
    this.#hideLoading();
    this.#hideError();
    
    // Determine which view to show
    if (this.#requiresLogin) {
      this.#showLoginView();
    } else {
      this.#showFormView();
    }
    
    super.showDialog();
  }

  /**
   * Handle back button - navigate based on current view
   */
  handleBack() {
    if (this.#currentView === 'wilaya') {
      this.#showFormView();
    } else if (this.#currentView === 'city') {
      this.#showFormView();
    } else {
      this.closeDialog();
    }
  }
  
  /**
   * Show the login required view (slide 0)
   */
  #showLoginView() {
    const slidesContainer = /** @type {HTMLElement} */ (this.refs.slidesContainer);
    const drawerTitle = /** @type {HTMLElement} */ (this.refs.drawerTitle);
    const loginSlide = /** @type {HTMLElement} */ (this.refs.loginSlide);
    const formSlide = /** @type {HTMLElement} */ (this.refs.formSlide);
    
    if (slidesContainer) {
      slidesContainer.style.transform = '';
      slidesContainer.classList.remove('show-wilaya', 'show-city');
      slidesContainer.classList.add('show-login');
    }
    if (drawerTitle) {
      drawerTitle.textContent = 'Connexion requise';
    }
    
    this.#currentView = 'login';
  }

  /**
   * Opens the wilaya selection (slide to wilaya list)
   */
  openWilayaSheet() {
    const slidesContainer = /** @type {HTMLElement} */ (this.refs.slidesContainer);
    const drawerTitle = /** @type {HTMLElement} */ (this.refs.drawerTitle);
    
    if (slidesContainer) {
      slidesContainer.style.transform = '';
      slidesContainer.classList.remove('show-login', 'show-city');
      slidesContainer.classList.add('show-wilaya');
    }
    if (drawerTitle) {
      drawerTitle.textContent = 'Sélectionnez votre wilaya';
    }
    this.#currentView = 'wilaya';
  }

  /**
   * Opens the city selection (slide to city list)
   */
  openCitySheet() {
    if (!this.#selectedWilayaCode) return;
    
    const slidesContainer = /** @type {HTMLElement} */ (this.refs.slidesContainer);
    const drawerTitle = /** @type {HTMLElement} */ (this.refs.drawerTitle);
    
    if (slidesContainer) {
      slidesContainer.style.transform = '';
      slidesContainer.classList.remove('show-login', 'show-wilaya');
      slidesContainer.classList.add('show-city');
    }
    if (drawerTitle) {
      drawerTitle.textContent = 'Sélectionnez votre commune';
    }
    this.#currentView = 'city';
  }

  /**
   * Closes the wilaya selection (slide back to form)
   */
  closeWilayaSheet() {
    this.#showFormView();
  }

  /**
   * Show the form view (slide 1) - THE DEFAULT VIEW FOR LOGGED IN USERS
   */
  #showFormView() {
    const slidesContainer = /** @type {HTMLElement} */ (this.refs.slidesContainer);
    const drawerTitle = /** @type {HTMLElement} */ (this.refs.drawerTitle);
    const loginSlide = /** @type {HTMLElement} */ (this.refs.loginSlide);
    const formSlide = /** @type {HTMLElement} */ (this.refs.formSlide);
    
    if (slidesContainer) {
      slidesContainer.style.transform = '';
      slidesContainer.classList.remove('show-wilaya', 'show-city');
      slidesContainer.classList.remove('show-login');
    }
    if (drawerTitle) {
      drawerTitle.textContent = 'Ajoutez votre adresse';
    }
    
    this.#currentView = 'form';
    
    // Pre-fill customer data
    this.#prefillFromCustomer();
  }

  /**
   * Handles wilaya selection
   * @param {Event} event
   */
  selectWilaya(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = /** @type {HTMLElement | null} */ (target?.closest('[data-wilaya]'));
    if (!button) return;

    const wilaya = button.dataset.wilaya ?? '';
    const wilayaCode = button.dataset.wilayaCode ?? wilaya.split('-')[0] ?? '';
    const wilayaInput = /** @type {HTMLInputElement | null} */ (this.refs.wilayaInput);
    const wilayaCodeInput = /** @type {HTMLInputElement | null} */ (this.refs.wilayaCodeInput);
    const wilayaText = /** @type {HTMLElement | null} */ (this.refs.wilayaText);
    const wilayaButton = /** @type {HTMLElement | null} */ (this.refs.wilayaButton);

    if (wilayaInput) wilayaInput.value = wilaya;
    if (wilayaCodeInput) wilayaCodeInput.value = wilayaCode;
    if (wilayaText) wilayaText.textContent = wilaya;
    if (wilayaButton) wilayaButton.classList.add('has-value');

    // Store selected wilaya code and populate city list
    this.#selectedWilayaCode = wilayaCode;
    this.#populateCityList(wilayaCode);
    
    // Reset city selection when wilaya changes
    this.#resetCitySelection();

    // Go directly to city selection view
    this.openCitySheet();
  }

  /**
   * Reset city selection when wilaya changes
   */
  #resetCitySelection() {
    const cityInput = /** @type {HTMLInputElement | null} */ (this.refs.cityInput);
    const cityIndexInput = /** @type {HTMLInputElement | null} */ (this.refs.cityIndexInput);
    const zipInput = /** @type {HTMLInputElement | null} */ (this.refs.zipInput);
    const cityText = /** @type {HTMLElement | null} */ (this.refs.cityText);
    const cityButton = /** @type {HTMLButtonElement | null} */ (this.refs.cityButton);
    
    if (cityInput) cityInput.value = '';
    if (cityIndexInput) cityIndexInput.value = '';
    if (zipInput) zipInput.value = '';
    if (cityText) cityText.textContent = 'Sélectionnez votre commune';
    if (cityButton) {
      cityButton.classList.remove('has-value');
      // Enable city button after wilaya is selected
      cityButton.disabled = false;
    }
  }

  /**
   * Populate city list based on selected wilaya (fast in-memory lookup)
   * @param {string} wilayaCode
   */
  #populateCityList(wilayaCode) {
    const cityList = /** @type {HTMLElement | null} */ (this.refs.cityList);
    
    // DEBUG: Log to help identify the issue
    console.log('[AddressDrawer] #populateCityList called with wilayaCode:', wilayaCode);
    console.log('[AddressDrawer] cityList element:', cityList);
    console.log('[AddressDrawer] window.getCommunesByWilaya exists:', typeof window.getCommunesByWilaya);
    console.log('[AddressDrawer] window.ALUX_WILAYAS exists:', !!window.ALUX_WILAYAS);
    
    if (!cityList) {
      console.error('[AddressDrawer] cityList ref not found!');
      return;
    }
    
    // Get communes from window.getCommunesByWilaya (O(1) lookup)
    // Now returns array of {name, zip} objects
    const communes = window.getCommunesByWilaya?.(wilayaCode) || [];
    
    // DEBUG: Log communes
    console.log('[AddressDrawer] communes for wilaya', wilayaCode, ':', communes);
    console.log('[AddressDrawer] communes length:', communes.length);
    
    // Build HTML in one go for performance (no DOM manipulation in loop)
    const html = communes.map((/** @type {CommuneData} */ commune, /** @type {number} */ index) => 
      `<button type="button" class="address-drawer__city-item" data-city="${commune.name}" data-city-zip="${commune.zip}" data-city-index="${index}">${commune.name}</button>`
    ).join('');
    
    console.log('[AddressDrawer] Generated HTML length:', html.length);
    
    cityList.innerHTML = html;
  }

  /**
   * Handles city selection
   * @param {Event} event
   */
  selectCity(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = /** @type {HTMLElement | null} */ (target?.closest('[data-city]'));
    if (!button) return;

    const city = button.dataset.city || '';
    const cityZip = button.dataset.cityZip || '';
    const cityIndex = parseInt(button.dataset.cityIndex || '0', 10);
    const cityInput = /** @type {HTMLInputElement | null} */ (this.refs.cityInput);
    const cityIndexInput = /** @type {HTMLInputElement | null} */ (this.refs.cityIndexInput);
    const zipInput = /** @type {HTMLInputElement | null} */ (this.refs.zipInput);
    const cityText = /** @type {HTMLElement | null} */ (this.refs.cityText);
    const cityButton = /** @type {HTMLElement | null} */ (this.refs.cityButton);

    if (cityInput) cityInput.value = city;
    if (cityIndexInput) cityIndexInput.value = String(cityIndex);
    if (cityText) cityText.textContent = city;
    if (cityButton) cityButton.classList.add('has-value');
    
    // Use the exact zip code from the database
    if (zipInput) {
      zipInput.value = cityZip;
    }

    // Go back to form view
    this.#showFormView();
  }

  /**
   * Handles form submission
   * @param {Event} event
   */
  submitAddress(event) {
    event.preventDefault();
    this.#hideError();

    const nameInput = /** @type {HTMLInputElement | null} */ (this.refs.nameInput);
    const emailInput = /** @type {HTMLInputElement | null} */ (this.refs.emailInput);
    const phoneInput = /** @type {HTMLInputElement | null} */ (this.refs.phoneInput);
    const addressInput = /** @type {HTMLInputElement | null} */ (this.refs.addressInput);
    const wilayaInput = /** @type {HTMLInputElement | null} */ (this.refs.wilayaInput);
    const wilayaCodeInput = /** @type {HTMLInputElement | null} */ (this.refs.wilayaCodeInput);
    const cityInput = /** @type {HTMLInputElement | null} */ (this.refs.cityInput);
    const cityIndexInput = /** @type {HTMLInputElement | null} */ (this.refs.cityIndexInput);
    const zipInput = /** @type {HTMLInputElement | null} */ (this.refs.zipInput);

    if (!nameInput?.value || !emailInput?.value || !phoneInput?.value || !addressInput?.value || !wilayaInput?.value) {
      this.#showError('Veuillez remplir tous les champs');
      return;
    }

    // Validate city is selected
    if (!cityInput?.value) {
      this.#showError('Veuillez sélectionner une commune');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput.value.trim())) {
      this.#showError('Adresse email invalide');
      return;
    }

    const phoneRegex = /^0[567]\d{8}$/;
    const cleanPhone = phoneInput.value.replace(/\s/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      this.#showError('Numéro de téléphone invalide. Format: 05/06/07 XX XX XX XX');
      return;
    }

    // Include customer ID if logged in, always include email (guest or logged in)
    const addressData = {
      name: nameInput.value,
      email: emailInput.value.trim(),
      phone: cleanPhone,
      address: addressInput.value,
      wilaya: wilayaInput.value,
      wilayaCode: wilayaCodeInput?.value || this.#selectedWilayaCode || '',
      city: cityInput.value,
      cityIndex: cityIndexInput?.value || '0',
      zip: zipInput?.value || '',
      timestamp: Date.now(),
      // Add customer ID if available
      ...(this.#customerId && { customerId: this.#customerId }),
      // Also include customer data from global object
      ...(AluxCustomer?.isLoggedIn && {
        customerId: AluxCustomer.id,
        customerEmail: AluxCustomer.email
      })
    };

    localStorage.setItem('cod_address', JSON.stringify(addressData));
    this.#showLoading();
    window.location.href = '/pages/order-summary';
  }

  #showLoading() {
    const submitButton = /** @type {HTMLElement | null} */ (this.refs.submitButton);
    const submitText = /** @type {HTMLElement | null} */ (this.refs.submitText);
    const spinner = /** @type {HTMLElement | null} */ (this.refs.spinner);
    if (submitButton) submitButton.classList.add('is-loading');
    if (submitText) submitText.hidden = true;
    if (spinner) spinner.hidden = false;
  }

  #hideLoading() {
    const submitButton = /** @type {HTMLElement | null} */ (this.refs.submitButton);
    const submitText = /** @type {HTMLElement | null} */ (this.refs.submitText);
    const spinner = /** @type {HTMLElement | null} */ (this.refs.spinner);
    if (submitButton) submitButton.classList.remove('is-loading');
    if (submitText) submitText.hidden = false;
    if (spinner) spinner.hidden = true;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const errorMessage = /** @type {HTMLElement | null} */ (this.refs.errorMessage);
    const errorText = /** @type {HTMLElement | null} */ (this.refs.errorText);
    if (errorMessage && errorText) {
      errorText.textContent = message;
      errorMessage.hidden = false;
    }
  }

  #hideError() {
    const errorMessage = /** @type {HTMLElement | null} */ (this.refs.errorMessage);
    if (errorMessage) errorMessage.hidden = true;
  }

  #loadSavedAddress() {
    const saved = localStorage.getItem('cod_address');
    if (!saved) return;

    try {
      const data = JSON.parse(saved);
      const nameInput = /** @type {HTMLInputElement | null} */ (this.refs.nameInput);
      const emailInput = /** @type {HTMLInputElement | null} */ (this.refs.emailInput);
      const phoneInput = /** @type {HTMLInputElement | null} */ (this.refs.phoneInput);
      const addressInput = /** @type {HTMLInputElement | null} */ (this.refs.addressInput);
      const wilayaInput = /** @type {HTMLInputElement | null} */ (this.refs.wilayaInput);
      const wilayaCodeInput = /** @type {HTMLInputElement | null} */ (this.refs.wilayaCodeInput);
      const wilayaText = /** @type {HTMLElement | null} */ (this.refs.wilayaText);
      const wilayaButton = /** @type {HTMLElement | null} */ (this.refs.wilayaButton);
      const cityInput = /** @type {HTMLInputElement | null} */ (this.refs.cityInput);
      const cityIndexInput = /** @type {HTMLInputElement | null} */ (this.refs.cityIndexInput);
      const zipInput = /** @type {HTMLInputElement | null} */ (this.refs.zipInput);
      const cityText = /** @type {HTMLElement | null} */ (this.refs.cityText);
      const cityButton = /** @type {HTMLButtonElement | null} */ (this.refs.cityButton);

      if (nameInput && data.name) nameInput.value = data.name;
      if (emailInput && data.email) emailInput.value = data.email;
      if (phoneInput && data.phone) phoneInput.value = data.phone;
      if (addressInput && data.address) addressInput.value = data.address;
      if (wilayaInput && data.wilaya) {
        wilayaInput.value = data.wilaya;
        if (wilayaText) wilayaText.textContent = data.wilaya;
        if (wilayaButton) wilayaButton.classList.add('has-value');
        
        // Store wilaya code and populate city list
        const wilayaCode = data.wilayaCode || data.wilaya.split('-')[0];
        if (wilayaCodeInput) wilayaCodeInput.value = wilayaCode;
        this.#selectedWilayaCode = wilayaCode;
        this.#populateCityList(wilayaCode);
        
        // Enable city button
        if (cityButton) cityButton.disabled = false;
      }
      
      // Restore city selection
      if (cityInput && data.city) {
        cityInput.value = data.city;
        if (cityText) cityText.textContent = data.city;
        if (cityButton) cityButton.classList.add('has-value');
      }
      if (cityIndexInput && data.cityIndex !== undefined) {
        cityIndexInput.value = String(data.cityIndex);
      }
      if (zipInput && data.zip) {
        zipInput.value = data.zip;
      }
    } catch (e) {
      console.error('Failed to load saved address:', e);
    }
  }
}

if (!customElements.get('address-drawer-component')) {
  customElements.define('address-drawer-component', AddressDrawerComponent);
}

export { AddressDrawerComponent };
